/**
 * Auto-pilot page registry.
 *
 * Each controllable page registers itself while mounted:
 *   - `page`: its id ('lineups', later 'playground', 'trade', 'projections')
 *   - `describeState()`: a compact snapshot the agent reads (page-awareness)
 *   - `actions`: named handlers the agent may invoke (page control)
 *
 * Only the mounted page is registered, so "which page am I on?" falls out of
 * the registry for free. The assistant sends the snapshot with every
 * auto-pilot turn and executes returned `ui_action` parts via these handlers.
 */
import { useEffect } from 'react';

export type AutopilotHandler = (args: any) => void | string | Promise<void | string>;

export interface AutopilotPage {
  page: string;
  describeState: () => any;
  actions: Record<string, AutopilotHandler>;
}

let active: AutopilotPage | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

export const autopilotRegistry = {
  getActive: (): AutopilotPage | null => active,

  register(reg: AutopilotPage) {
    active = reg;
    notify();
  },

  unregister(page: string) {
    if (active?.page === page) {
      active = null;
      notify();
    }
  },

  /** Subscribe to registration changes (for the widget's page-awareness chip). */
  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  /** Execute a named action on the active page. Returns an optional result note. */
  async execute(action: string, args: any): Promise<string | void> {
    if (!active) throw new Error('No auto-pilot page is active');
    const handler = active.actions[action];
    if (!handler) throw new Error(`Action "${action}" is not available on this page`);
    return handler(args ?? {});
  },
};

/**
 * Register a page for auto-pilot while it is mounted.
 * Re-registers every render so `describeState`/handlers close over fresh state.
 */
export function useAutopilotPage(reg: AutopilotPage): void {
  useEffect(() => {
    autopilotRegistry.register(reg);
    return () => autopilotRegistry.unregister(reg.page);
  });
}
